import React, { useEffect, useState } from 'react';
import { ProjectRow } from '../../lib/types';
import { getProjects, getProject } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import AssessmentForm from '../components/assessment-form';
import ApprovalForm from '../components/approval-form';
import styles from './governance.module.css';

export default function GovernancePage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectRow | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Detect which tab to show based on current path
  const currentPath = window.location.hash.startsWith('#/')
    ? window.location.hash.substring(2)
    : window.location.hash.substring(1);
  const [activeTab, setActiveTab] = useState<'assessment' | 'decisions'>(
    currentPath === '/gate-decisions' ? 'decisions' : 'assessment'
  );

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    // Load selected project details when selectedProjectId changes
    if (selectedProjectId) {
      loadSelectedProject(selectedProjectId);
    } else {
      setSelectedProject(null);
    }
  }, [selectedProjectId]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await getProjects();
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSelectedProject = async (projectId: string) => {
    try {
      const response = await getProject(projectId);
      if (response.data) {
        setSelectedProject(response.data);
      }
    } catch (error) {
      console.error('Failed to load project details:', error);
      setSelectedProject(null);
    }
  };

  // Projects ready for governance assessment
  const projectsForAssessment = projects.filter(
    p => p.Status === 'ReadyForGovernance'
  );

  // Projects under governance review (decision needed)
  const projectsUnderReview = projects.filter(
    p => p.Status === 'UnderGovernanceReview'
  );

  const handleFormSuccess = async () => {
    // Refresh the projects list after successful submission
    await fetchProjects();
    // Clear the selected project and close the detail panel
    setSelectedProjectId(null);
    setSubmitting(false);
  };

  const handleFormCancel = () => {
    setSelectedProjectId(null);
    setSubmitting(false);
  };

  if (!user) {
    return <div className="error">Not authenticated</div>;
  }

  return (
      <div className={styles.governanceContainer}>
        {/* Tab Navigation */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'assessment' ? styles.active : ''}`}
            onClick={() => setActiveTab('assessment')}
          >
            <span className={styles.tabIcon}>🎯</span>
            Assessment ({projectsForAssessment.length})
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'decisions' ? styles.active : ''}`}
            onClick={() => setActiveTab('decisions')}
          >
            <span className={styles.tabIcon}>⚖️</span>
            Gate Decisions ({projectsUnderReview.length})
          </button>
        </div>

        {/* Content Area */}
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>Loading governance items...</div>
          ) : activeTab === 'assessment' ? (
            // Assessment Tab
            <div className={styles.assessmentView}>
              {projectsForAssessment.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No ideas ready for assessment</p>
                </div>
              ) : (
                <div className={styles.projectsList}>
                  {projectsForAssessment.map(project => (
                    <div
                      key={project.ProjectId}
                      className={`${styles.projectCard} ${selectedProjectId === project.ProjectId ? styles.selected : ''}`}
                      onClick={() => setSelectedProjectId(project.ProjectId)}
                    >
                      <div className={styles.cardHeader}>
                        <h3 className={styles.cardTitle}>{project.Title}</h3>
                        <span className={styles.projectId}>{project.ProjectId}</span>
                      </div>
                      <p className={styles.cardDescription}>{project.Description}</p>
                      <div className={styles.cardMeta}>
                        <span className={styles.domain}>{project.Domain}</span>
                        <span className={styles.type}>{project.WorkType}</span>
                        <span className={styles.submitter}>by {project.CreatedByName}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Gate Decisions Tab
            <div className={styles.decisionsView}>
              {projectsUnderReview.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No projects awaiting gate decisions</p>
                </div>
              ) : (
                <div className={styles.projectsList}>
                  {projectsUnderReview.map(project => (
                    <div
                      key={project.ProjectId}
                      className={`${styles.projectCard} ${selectedProjectId === project.ProjectId ? styles.selected : ''}`}
                      onClick={() => setSelectedProjectId(project.ProjectId)}
                    >
                      <div className={styles.cardHeader}>
                        <h3 className={styles.cardTitle}>{project.Title}</h3>
                        <span className={styles.projectId}>{project.ProjectId}</span>
                      </div>
                      <p className={styles.cardDescription}>{project.Description}</p>
                      <div className={styles.cardMeta}>
                        <span className={styles.domain}>{project.Domain}</span>
                        <span className={styles.priority}>{project.Priority || 'TBD'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedProjectId && selectedProject && (
          <div className={styles.detailPanel}>
            <button
              className={styles.closeButton}
              onClick={() => setSelectedProjectId(null)}
            >
              ×
            </button>

            <div className={styles.detailHeader}>
              <h2 className={styles.detailTitle}>{selectedProject.Title}</h2>
              <p className={styles.detailId}>{selectedProjectId}</p>
            </div>

            <div className={styles.detailContent}>
              {activeTab === 'assessment' ? (
                <AssessmentForm
                  projectId={selectedProjectId}
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                />
              ) : (
                <ApprovalForm
                  projectId={selectedProjectId}
                  isAboveTheLine={selectedProject.Status === 'AboveTheLine'}
                  onSubmitSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                />
              )}
            </div>
          </div>
        )}
      </div>
  );
}
